import { checkHasProfit } from '@design/charts/helpers/chart-values/chart-values';
import { TextWithBgAlign } from '@design/charts/text-with-bg/text-with-bg-utils';
import { TextWithBg } from '@design/charts/text-with-bg/TextWithBg';
import clsx from 'clsx';
import React, { useId, useState } from 'react';

import { TextLabel } from '../text-label/TextLabel';
import styles from './HighLowBar.module.scss';

export interface HighLowMedianDataPoint {
  high: number;
  low: number;
  median: number;
  label?: string;
  /** API horizon key (e.g. `0d` for earnings day); used for chart markers. */
  horizon?: string;
}

export interface HighLowBarProps {
  dataPoint: HighLowMedianDataPoint;
  yHigh: number;
  yLow: number;
  yMedian: number;
  y0: number;
  showMedianLabel?: boolean;
  enableMouseInteraction?: boolean;
  numberFormatter: (n: number) => string;
  hasTextWithBg?: boolean;
}

const BAR_WIDTH_PX = 9;
const BAR_HALF_WIDTH_PX = BAR_WIDTH_PX / 2;
const HIGH_LOW_VALUE_PADDING = 4;
const MEDIAN_LABEL_X_OFFSET = 18;

export const HighLowBar = ({
  dataPoint,
  yHigh,
  yLow,
  yMedian,
  y0,
  showMedianLabel = true,
  enableMouseInteraction = true,
  numberFormatter,
  hasTextWithBg = false,
}: HighLowBarProps) => {
  const [showHighLowValues, setShowHighLowValues] = useState(false);
  const clipPathId = useId();
  const hasMedianProfit = checkHasProfit(dataPoint.median);

  const highStart = yHigh;
  const highEnd = dataPoint.low > 0 ? yLow : y0;
  const highHeight = highEnd - highStart;

  const lowStart = dataPoint.high < 0 ? yHigh : y0;
  const lowEnd = yLow;
  const lowHeight = lowEnd - lowStart;

  const fullStart = yHigh;
  const fullHeight = lowEnd - highStart;

  const highLabelY = highStart - HIGH_LOW_VALUE_PADDING;
  const lowLabelY = lowEnd + HIGH_LOW_VALUE_PADDING;

  const onMouseEnter = () => {
    setShowHighLowValues(true);
  };

  const onMouseLeave = () => {
    setShowHighLowValues(false);
  };

  return (
    <g
      data-testid="high-low-bar"
      onMouseEnter={enableMouseInteraction ? onMouseEnter : undefined}
      onMouseLeave={enableMouseInteraction ? onMouseLeave : undefined}
    >
      <clipPath id={clipPathId}>
        <rect
          width={BAR_WIDTH_PX}
          x={-BAR_HALF_WIDTH_PX}
          y={fullStart}
          height={fullHeight}
          rx={BAR_HALF_WIDTH_PX}
        />
      </clipPath>

      <g clipPath={`url(#${clipPathId})`}>
        {dataPoint.high > 0 && (
          <rect
            className={clsx(styles.rect, {
              [styles.rectProfit]: checkHasProfit(dataPoint.high),
            })}
            data-testid="high-bar"
            width={BAR_WIDTH_PX}
            x={-BAR_HALF_WIDTH_PX}
            y={highStart}
            height={highHeight}
          />
        )}

        {dataPoint.low < 0 && (
          <rect
            className={clsx(styles.rect, {
              [styles.rectProfit]: checkHasProfit(dataPoint.low),
            })}
            data-testid="low-bar"
            width={BAR_WIDTH_PX}
            x={-BAR_HALF_WIDTH_PX}
            y={lowStart}
            height={lowHeight}
          />
        )}
      </g>

      <g data-testid="median-label" transform={`translate(0, ${yMedian})`}>
        <g className={styles.animatedGroup}>
          <circle className={styles.circle} r={BAR_HALF_WIDTH_PX / 2} />
          {showMedianLabel && (
            <TextLabel
              hasProfit={hasMedianProfit}
              label={numberFormatter(dataPoint.median)}
              x={BAR_WIDTH_PX + MEDIAN_LABEL_X_OFFSET}
            />
          )}
        </g>
      </g>

      {showHighLowValues &&
        (hasTextWithBg ? (
          <>
            <TextWithBg
              className={clsx(styles.styledTextWithBg, {
                [styles.styledTextWithBgProfit]: checkHasProfit(dataPoint.high),
              })}
              position={TextWithBgAlign.Top}
              translateX={0}
              translateY={highLabelY}
              data-testid="high-value-with-bg"
            >
              {numberFormatter(dataPoint.high)}
            </TextWithBg>
            {!showMedianLabel && (
              <text
                className={styles.medianValue}
                textAnchor="start"
                alignmentBaseline="middle"
                x={BAR_WIDTH_PX + MEDIAN_LABEL_X_OFFSET}
                y={yMedian}
                data-testid="median-value-with-bg"
              >
                {numberFormatter(dataPoint.median)}
              </text>
            )}
            <TextWithBg
              className={clsx(styles.styledTextWithBg, {
                [styles.styledTextWithBgProfit]: checkHasProfit(dataPoint.low),
              })}
              position={TextWithBgAlign.Bottom}
              translateX={0}
              translateY={lowLabelY}
              data-testid="low-value-with-bg"
            >
              {numberFormatter(dataPoint.low)}
            </TextWithBg>
          </>
        ) : (
          <>
            <text
              className={clsx(styles.value, {
                [styles.valueProfit]: checkHasProfit(dataPoint.high),
              })}
              textAnchor="middle"
              dominantBaseline="text-after-edge"
              y={highLabelY}
              data-testid="high-value"
            >
              {numberFormatter(dataPoint.high)}
            </text>
            {!showMedianLabel && (
              <text
                className={styles.medianValue}
                textAnchor="start"
                alignmentBaseline="middle"
                x={BAR_WIDTH_PX + MEDIAN_LABEL_X_OFFSET}
                y={yMedian}
                data-testid="median-value"
              >
                {numberFormatter(dataPoint.median)}
              </text>
            )}
            <text
              className={clsx(styles.value, {
                [styles.valueProfit]: checkHasProfit(dataPoint.low),
              })}
              alignmentBaseline="before-edge"
              textAnchor="middle"
              y={lowLabelY}
              data-testid="low-value"
            >
              {numberFormatter(dataPoint.low)}
            </text>
          </>
        ))}
    </g>
  );
};
